import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { MapPin, ArrowLeft, ArrowClockwise, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { useMutation, useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { identifyUser, trackEvent } from "@shared/lib/posthog-lazy";
import { USER_SESSION_KEY } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/auth/verify")({
  component: VerifyPage,
});

interface PendingVerification {
  userId: string;
  phone: string;
  email: string;
  isLogin?: boolean;
  isRegister?: boolean;
  rememberMe?: boolean;
}

function VerifyPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [pendingData, setPendingData] = useState<PendingVerification | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Convex mutations
  const verifyOTP = useMutation(api.auth.verifyOTP);
  const sendOTP = useMutation(api.auth.sendOTP);
  const verifyPhone = useMutation(api.auth.verifyPhone);
  const verifyTwilioOTP = useAction(api.auth.verifyTwilioOTP);
  
  // Notă: Verificarea admin se face în layout-ul admin, nu aici

  // Citește datele din sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem("pendingVerification");
    if (stored) {
      try {
        const data = JSON.parse(stored) as PendingVerification;
        setPendingData(data);
      } catch (e) {
        console.error("Error parsing pending verification:", e);
      }
    }
  }, []);

  // Countdown pentru retrimitere
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Focus pe primul input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Formatare număr telefon pentru afișare
  const formatPhone = (phone: string): string => {
    if (!phone) return "+40 7XX XXX XXX";
    // Ascunde parțial numărul pentru securitate
    if (phone.length >= 10) {
      const last4 = phone.slice(-4);
      const prefix = phone.slice(0, phone.length - 7);
      return `${prefix}XXX ${last4}`;
    }
    return phone;
  };

  const handleCodeChange = (index: number, value: string) => {
    // Permite doar cifre
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);

    // Auto-focus pe următorul input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit când toate câmpurile sunt completate
    if (newCode.every((c) => c) && newCode.join("").length === 6) {
      handleSubmit(newCode.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Backspace - șterge și mergi înapoi
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Arrow keys
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleSubmit(pastedData);
    }
  };

  const handleSubmit = async (otpCode: string) => {
    if (!pendingData) {
      setError("generic");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Verifică OTP-ul cu Convex
      const result = await verifyOTP({
        phone: pendingData.phone,
        code: otpCode,
        type: "verify",
      });

      // Sesiunea vine de pe oricare dintre cele două căi de verificare.
      let sessionToken: string | null =
        (result as { sessionToken?: string | null }).sessionToken ?? null;

      if (!result.success) {
        // Dacă e Twilio Verify, verifică via Twilio API
        if (result.requiresTwilioVerify) {
          console.log("[Verify] Using Twilio Verify API");
          const twilioResult = await verifyTwilioOTP({
            phone: pendingData.phone,
            code: otpCode,
            type: "verify",
          });

          sessionToken = twilioResult.sessionToken ?? null;

          if (!twilioResult.success) {
            setError(twilioResult.error || "invalidCode");
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
            setIsLoading(false);
            return;
          }
          // Twilio Verify success - continuă cu verifyPhone
        } else {
          setError(result.error || "invalidCode");
          setCode(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
          setIsLoading(false);
          return;
        }
      }

      // Fără sesiune nu se poate continua: verifyPhone o cere, tocmai ca
      // nimeni să nu poată marca un telefon străin ca verificat.
      if (!sessionToken) {
        setError("generic");
        setIsLoading(false);
        return;
      }

      // Marchează telefonul ca verificat
      const verifyPhoneResult = await verifyPhone({
        userId: pendingData.userId as Id<"users">,
        sessionToken,
      });

      if (!verifyPhoneResult.success) {
        console.error("[Verify] verifyPhone failed:", verifyPhoneResult.error);
        setError(verifyPhoneResult.error || "generic");
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        setIsLoading(false);
        return;
      }

      // Succes!
      setSuccess(true);

      // Salvează userId în localStorage pentru sesiune
      try {
        localStorage.setItem(USER_SESSION_KEY, sessionToken);
        localStorage.setItem("userId", pendingData.userId);
      } catch (err) {
        console.error("[Verify] Failed to save userId to localStorage:", err);
        // Continuă chiar dacă localStorage eșuează
      }

      // Identificare PostHog (nu blochează flow-ul dacă eșuează)
      try {
        await identifyUser(pendingData.userId as Id<"users">, {
          email: pendingData.email,
          phone: pendingData.phone,
          isRegister: pendingData.isRegister || false,
          isLogin: pendingData.isLogin || false,
        });

        // Tracking eveniment
        await trackEvent(pendingData.isRegister ? "user_registered" : "user_logged_in", {
          email: pendingData.email,
          phone: pendingData.phone,
        });
      } catch (err) {
        console.error("[Verify] PostHog tracking failed:", err);
        // Nu blochează flow-ul dacă PostHog eșuează
      }

      // Șterge datele temporare
      try {
        sessionStorage.removeItem("pendingVerification");
      } catch (err) {
        console.error("[Verify] Failed to clear sessionStorage:", err);
        // Nu e critic dacă eșuează
      }

      // Redirecționează - verificarea admin se face în layout-ul admin
      // Dacă utilizatorul este admin, va putea accesa /admin, altfel va fi redirecționat la /app
      setTimeout(() => {
        navigate({ to: "/app" });
      }, 1500);

    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError("generic");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || !pendingData) return;

    setCanResend(false);
    setCountdown(60);
    setError(null);

    try {
      const result = await sendOTP({
        phone: pendingData.phone,
        type: "verify",
      });

      if (!result.success) {
        console.error("[Verify] Resend OTP failed:", result.error);
        if (result.retryAfter) {
          setError("rateLimit.otpTooManyRequests");
          setCountdown(result.retryAfter);
        } else {
          setError(result.error || "generic");
        }
        setCanResend(true);
      }
      // Dacă reușește, countdown-ul va continua automat
    } catch (err) {
      console.error("[Verify] Error resending OTP:", err);
      setError("generic");
      setCanResend(true);
    }
  };

  // Dacă nu sunt date de verificare, redirecționează
  if (!pendingData && typeof window !== "undefined") {
    const stored = sessionStorage.getItem("pendingVerification");
    if (!stored) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
          <div className="text-center">
            <WarningCircle size={16} className="text-error mx-auto mb-4" />
            <h1 className="text-xl font-bold text-text mb-2">
              {t("common:errors.generic")}
            </h1>
            <p className="text-text-light mb-6">
              Nu există date de verificare. Te rugăm să te înregistrezi din nou.
            </p>
            <Link
              to="/auth/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-gradient text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-primary/30 transition-all"
            >
              Înapoi la înregistrare
            </Link>
          </div>
        </div>
      );
    }
  }

  // Ecran de succes
  if (success) {
    const isLogin = pendingData?.isLogin === true;
    const isRegister = pendingData?.isRegister === true;
    
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={10} className="text-success" />
          </div>
          <h1 className="text-2xl font-bold text-text mb-2">
            {t("success.verified")}
          </h1>
          <p className="text-text-light mb-4">
            {isLogin 
              ? t("success.loggedIn", { defaultValue: "Te-ai autentificat cu succes!" })
              : isRegister
              ? t("success.registered", { defaultValue: "Contul tău a fost creat cu succes!" })
              : t("success.verified", { defaultValue: "Verificat cu succes!" })
            }
          </p>
          <p className="text-text-muted text-sm">
            {t("verify.redirecting", { defaultValue: "Redirecționare către aplicație..." })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link
          to="/auth/register"
          className="inline-flex items-center gap-2 text-text-light hover:text-text transition-colors"
        >
          <ArrowLeft size={20} />
          {t("common:actions.back")}
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
              <MapPin size={9} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-text">{t("verify.title")}</h1>
            <p className="text-text-light mt-1">{t("verify.subtitle")}</p>
          </div>

          {/* Info */}
          <div className="text-center mb-8">
            <p className="text-text-light">
              {t("verify.codeSentTo")}
            </p>
            <p className="text-text font-medium mt-1">
              {formatPhone(pendingData?.phone || "")}
            </p>
            {pendingData?.email && (
              <p className="text-text-muted text-sm mt-1">
                {pendingData.email}
              </p>
            )}
          </div>

          {/* OTP Input */}
          <div className="flex justify-center gap-3 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el: HTMLInputElement | null) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                className={`
                  w-12 h-14 text-center text-xl font-bold rounded-xl border-2
                  bg-white transition-all
                  focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${error ? "border-error" : "border-border"}
                `}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <WarningCircle size={16} className="text-error" />
              <p className="text-error text-sm">
                {t(`errors.${error}`, { defaultValue: t("common:errors.generic") })}
              </p>
            </div>
          )}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-center mb-6">
              <div className="w-6 h-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}


          {/* Resend button */}
          <div className="text-center">
            {canResend ? (
              <button
                onClick={handleResend}
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                <ArrowClockwise size={16} />
                {t("verify.resend")}
              </button>
            ) : (
              <p className="text-text-light">
                {t("verify.resendIn", { seconds: countdown })}
              </p>
            )}
          </div>

          {/* Change number link */}
          <div className="mt-8 text-center">
            <Link
              to="/auth/register"
              className="text-text-light hover:text-primary transition-colors text-sm"
            >
              {t("verify.changeNumber")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
